import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Camera, X } from 'lucide-react';

const PropertyImageGallery = ({ images = [], propertyAddress = '' }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!images || images.length === 0) {
    return (
      <div className="bg-gray-700 border border-gray-600 p-6 rounded-lg">
        <div className="flex items-center justify-center h-48 bg-gray-600 rounded-lg">
          <div className="text-center">
            <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-400">No images available</p>
          </div>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="bg-gray-700 border border-gray-600 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-semibold text-blue-300 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Property Images ({images.length})
          </h4>
        </div>
        
        {/* Main Image Display */}
        <div className="relative">
          <div className="h-64 bg-gray-600 rounded-lg overflow-hidden">
            <img
              src={images[currentImageIndex]}
              alt={`Property view ${currentImageIndex + 1}`}
              className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={openModal}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full transition-all duration-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full transition-all duration-200"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-3 py-2 rounded-lg">
            <div className="text-sm font-medium">
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>
        </div>

        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {images.slice(0, 10).map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`relative flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all duration-200 ${
                  index === currentImageIndex 
                    ? 'border-blue-400 ring-2 ring-blue-400' 
                    : 'border-gray-500 hover:border-gray-400'
                }`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </button>
            ))}
            {images.length > 10 && (
              <div className="flex-shrink-0 w-16 h-16 bg-gray-600 rounded flex items-center justify-center text-gray-300 text-sm">
                +{images.length - 10}
              </div>
            )}
          </div>
        )}

        {/* Full Screen Button */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={openModal}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <Camera className="w-4 h-4" />
            Full Screen View
          </button>
        </div>
      </div>

      {/* Full Screen Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-98 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center p-4">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-3 rounded-full transition-all"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="relative max-w-7xl max-h-full">
              <img
                src={images[currentImageIndex]}
                alt={`${propertyAddress} - Image ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />

              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-4 rounded-full transition-all"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-4 rounded-full transition-all"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white px-6 py-3 rounded-lg">
                <div className="text-center">
                  <div className="font-medium">{currentImageIndex + 1} of {images.length}</div>
                  <div className="text-sm text-gray-300">{propertyAddress}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PropertyImageGallery;